import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

const TestCard = ({ test, tests, onStartTest, onPayment, variant = 'detailed' }) => {
  const navigate = useNavigate();
  const isDetailed = variant === 'detailed';
  const [isMobile, setIsMobile] = useState(false);
  
  // Check if screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // If we have multiple tests, use the first one as the base and manage level selection
  const hasMultipleLevels = tests && tests.length > 1;
  const [selectedLevelIndex, setSelectedLevelIndex] = useState(0);
  
  // Get the currently selected test
  const currentTest = hasMultipleLevels ? tests[selectedLevelIndex] : test;
  
  // Get all available levels for this test
  const availableLevels = hasMultipleLevels ? tests.map(t => t.level).filter(Boolean) : [];

  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'easy':
        return 'var(--success)';
      case 'medium':
        return 'var(--warning)';
      case 'hard':
        return 'var(--error)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const getLevelIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'easy':
        return '🟢';
      case 'medium':
        return '🟡';
      case 'hard':
        return '🔴';
      default:
        return '⚪';
    }
  };

  const calculateScore = () => {
    if (!currentTest.numCorrectAnswers || !currentTest.numQuestions) return 0;
    return Math.round((currentTest.numCorrectAnswers / currentTest.numQuestions) * 100);
  };

  const getTestIcon = () => {
    switch (currentTest.status?.toLowerCase()) {
      case 'completed':
        return '🎯';
      case 'subscribed':
        return '📝';
      case 'unsubscribed':
        // Show lock only if price > 0, otherwise show pen
        return currentTest.price > 0 ? '🔒' : '📝';
      default:
        return '📝';
    }
  };

  const handleLevelChange = (index) => {
    setSelectedLevelIndex(index);
  };

  if (!isDetailed) {
    // Compact card design for my-tests page
    return (
      <div className="card" style={{
        padding: 'var(--spacing-lg)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        margin: '0'
      }}>
        {/* Header Section */}
        <div style={{ marginBottom: 'var(--spacing-md)' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-sm)'
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              color: 'var(--text-primary)',
              flex: 1
            }}>
              {currentTest.name || 'Test'}
            </h3>
            <span style={{ fontSize: '24px' }}>{getTestIcon()}</span>
          </div>
          
          {/* Level Toggle for multiple levels */}
          {hasMultipleLevels && (
            <div style={{
              display: 'flex',
              gap: 'var(--spacing-xs)',
              marginBottom: 'var(--spacing-sm)',
              flexWrap: 'wrap'
            }}>
              {availableLevels.map((level, index) => (
                <button
                  key={level}
                  onClick={() => handleLevelChange(index)}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-sm)',
                    background: selectedLevelIndex === index ? getLevelColor(level) : 'transparent',
                    color: selectedLevelIndex === index ? 'white' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                >
                  {getLevelIcon(level)} {level}
                </button>
              ))}
            </div>
          )}

          {/* Single Level Badge - moved to top for consistency */}
          {!hasMultipleLevels && currentTest.level && (
            <div style={{ marginBottom: 'var(--spacing-sm)' }}>
              <span className="badge" style={{
                backgroundColor: getLevelColor(currentTest.level),
                color: 'white',
                padding: '4px 8px',
                fontSize: 'var(--font-size-sm)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontWeight: '600',
                borderRadius: 'var(--radius-sm)'
              }}>
                {getLevelIcon(currentTest.level)} {currentTest.level}
              </span>
            </div>
          )}
        </div>

        {/* Description */}
        <div style={{ 
          marginBottom: 'var(--spacing-md)',
          flex: 1
        }}>
          <p style={{ 
            color: 'var(--text-secondary)',
            margin: 0,
            lineHeight: 1.5,
            fontSize: 'var(--font-size-sm)'
          }}>
            {currentTest.description || 'Take this test to assess your knowledge and improve your skills.'}
          </p>
        </div>

        {/* Test Details */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-sm)',
          marginBottom: 'var(--spacing-md)'
        }}>
          {/* Debug info - remove after testing */}
          {console.log('TestCard Debug:', {
            testId: currentTest.testId,
            status: currentTest.status,
            numQuestions: currentTest.numQuestions,
            totalQuestions: currentTest.totalQuestions,
            shouldShowQuestions: currentTest.numQuestions && currentTest.status === 'UNSUBSCRIBED',
            allProps: Object.keys(currentTest),
            fullTest: currentTest
          })}
          
          {(currentTest.numQuestions || currentTest.totalQuestions) && (currentTest.status === 'UNSUBSCRIBED' || currentTest.status === 'SUBSCRIBED') && (
            <span className="badge" style={{
              backgroundColor: 'transparent',
              color: 'var(--primary-color)',
              padding: '4px 8px',
              fontSize: 'var(--font-size-sm)',
              border: '1px solid var(--primary-color)',
              borderRadius: 'var(--radius-sm)',
              fontWeight: '600'
            }}>
              📝 {currentTest.numQuestions || currentTest.totalQuestions} Questions
            </span>
          )}
          {currentTest.suggestedTimeInMinutes && currentTest.status !== 'COMPLETED' && (
            <span className="badge" style={{
              backgroundColor: 'transparent',
              color: 'var(--primary-color)',
              padding: '4px 8px',
              fontSize: 'var(--font-size-sm)',
              border: '1px solid var(--primary-color)',
              borderRadius: 'var(--radius-sm)',
              fontWeight: '600'
            }}>
              ⏱️ {currentTest.suggestedTimeInMinutes} min
            </span>
          )}
        </div>

        {/* Completion Status */}
        {currentTest.status === 'COMPLETED' && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--spacing-sm)',
            marginBottom: 'var(--spacing-md)'
          }}>
            <span className="badge" style={{
              backgroundColor: 'var(--success-light)',
              color: 'var(--success)',
              padding: '4px 8px',
              fontSize: 'var(--font-size-sm)',
              border: '1px solid var(--success)',
              borderRadius: 'var(--radius-sm)',
              fontWeight: '600'
            }}>
              🎯 Score: {calculateScore()}%
            </span>
          </div>
        )}

        {/* Action Button */}
        <div style={{ marginTop: 'auto' }}>
          {currentTest.status === 'UNSUBSCRIBED' ? (
            currentTest.price ? (
              <button
                onClick={() => onPayment(currentTest)}
                className="button"
                style={{ 
                  width: '100%', 
                  backgroundColor: 'var(--secondary-color)',
                  padding: 'var(--spacing-sm) var(--spacing-md)'
                }}
              >
                Subscribe for ₹{currentTest.price}
              </button>
            ) : (
              <button
                onClick={() => onStartTest(currentTest.testId, currentTest.status)}
                className="button"
                style={{ 
                  width: '100%',
                  padding: 'var(--spacing-sm) var(--spacing-md)'
                }}
              >
                Start Test
              </button>
            )
          ) : currentTest.status === 'SUBSCRIBED' ? (
            <button
              onClick={() => onStartTest(currentTest.testId, currentTest.status)}
              className="button"
              style={{ 
                width: '100%',
                padding: 'var(--spacing-sm) var(--spacing-md)'
              }}
            >
              Start Test
            </button>
          ) : (
            <button
              onClick={() => onStartTest(currentTest.testId, currentTest.status)}
              className="button"
              style={{ 
                width: '100%', 
                backgroundColor: 'var(--success)',
                padding: 'var(--spacing-sm) var(--spacing-md)'
              }}
            >
              View Results
            </button>
          )}
        </div>
      </div>
    );
  }

  // Detailed card design for homepage
  return (
    <div className="card" style={{
      backgroundColor: 'white',
      padding: isMobile ? 'var(--spacing-lg)' : 'var(--spacing-xl)',
      marginBottom: 'var(--spacing-xl)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      ...(isMobile && {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      })
    }}>
      {/* Header Section */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--spacing-lg)',
        marginBottom: 'var(--spacing-lg)',
        flexWrap: 'wrap',
        ...(isMobile && {
          marginBottom: 'var(--spacing-md)',
          flexDirection: 'column',
          gap: 'var(--spacing-sm)'
        })
      }}>
        {/* Icon and Title Row for Mobile */}
        {isMobile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-sm)'
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              color: 'var(--text-primary)',
              flex: 1
            }}>
              {currentTest.name || 'Test'}
            </h3>
            <div style={{
              backgroundColor: 'var(--primary-light)',
              borderRadius: '50%',
              padding: 'var(--spacing-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <span style={{ fontSize: '24px' }}>{getTestIcon()}</span>
            </div>
          </div>
        )}

        {/* Desktop Icon */}
        {!isMobile && (
          <div style={{
            backgroundColor: 'var(--primary-light)',
            borderRadius: '50%',
            padding: 'var(--spacing-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <span style={{ fontSize: '28px' }}>{getTestIcon()}</span>
          </div>
        )}

        {/* Title and Level Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Desktop Title */}
          {!isMobile && (
            <h3 style={{
              margin: '0 0 var(--spacing-sm) 0',
              fontSize: 'var(--font-size-xl)',
              color: 'var(--text-primary)',
              fontWeight: '600'
            }}>
              {currentTest.name || 'Test'}
            </h3>
          )}
          
          {/* Level Toggle for multiple levels */}
          {hasMultipleLevels && (
            <div style={{
              display: 'flex',
              gap: 'var(--spacing-xs)',
              marginBottom: 'var(--spacing-sm)',
              flexWrap: 'wrap'
            }}>
              {availableLevels.map((level, index) => (
                <button
                  key={level}
                  onClick={() => handleLevelChange(index)}
                  style={{
                    padding: isMobile ? '4px 8px' : '8px 16px',
                    border: isMobile ? '1px solid var(--border-color)' : '2px solid var(--border-color)',
                    borderRadius: isMobile ? 'var(--radius-sm)' : 'var(--radius-md)',
                    background: selectedLevelIndex === index ? getLevelColor(level) : 'transparent',
                    color: selectedLevelIndex === index ? 'white' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '4px' : '6px',
                    transition: 'all 0.2s',
                    fontWeight: selectedLevelIndex === index ? '600' : '500'
                  }}
                >
                  {getLevelIcon(level)} {level}
                </button>
              ))}
            </div>
          )}

          {/* Single Level Badge */}
          {!hasMultipleLevels && currentTest.level && (
            <div style={{ marginBottom: 'var(--spacing-sm)' }}>
              <span className="badge" style={{
                backgroundColor: getLevelColor(currentTest.level),
                color: 'white',
                padding: isMobile ? '4px 8px' : '6px 12px',
                fontSize: 'var(--font-size-sm)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: isMobile ? '4px' : '6px',
                fontWeight: '600',
                borderRadius: isMobile ? 'var(--radius-sm)' : 'var(--radius-md)'
              }}>
                {getLevelIcon(currentTest.level)} {currentTest.level}
              </span>
            </div>
          )}
        </div>

        {/* Action Button */}
        <div style={{ 
          flexShrink: 0,
          ...(isMobile && {
            width: '100%',
            marginTop: 'var(--spacing-sm)'
          })
        }}>
          {currentTest.status === 'UNSUBSCRIBED' ? (
            currentTest.price ? (
              <button
                onClick={() => onPayment(currentTest)}
                className="button"
                style={{ 
                  width: '100%',
                  backgroundColor: 'var(--secondary-color)',
                  padding: isMobile ? 'var(--spacing-sm) var(--spacing-md)' : 'var(--spacing-md) var(--spacing-lg)',
                  fontSize: isMobile ? 'var(--font-size-sm)' : 'var(--font-size-md)',
                  fontWeight: '600',
                  ...(isMobile && { width: '100%' })
                }}
              >
                Subscribe for ₹{currentTest.price}
              </button>
            ) : (
              <button
                onClick={() => onStartTest(currentTest.testId, currentTest.status)}
                className="button"
                style={{ 
                  padding: isMobile ? 'var(--spacing-sm) var(--spacing-md)' : 'var(--spacing-md) var(--spacing-lg)',
                  fontSize: isMobile ? 'var(--font-size-sm)' : 'var(--font-size-md)',
                  fontWeight: '600',
                  ...(isMobile && { width: '100%' })
                }}
              >
                Start Test
              </button>
            )
          ) : currentTest.status === 'SUBSCRIBED' ? (
            <button
              onClick={() => onStartTest(currentTest.testId, currentTest.status)}
              className="button"
              style={{ 
                padding: isMobile ? 'var(--spacing-sm) var(--spacing-md)' : 'var(--spacing-md) var(--spacing-lg)',
                fontSize: isMobile ? 'var(--font-size-sm)' : 'var(--font-size-md)',
                fontWeight: '600',
                ...(isMobile && { width: '100%' })
              }}
            >
              Start Test
            </button>
          ) : (
            <button
              onClick={() => onStartTest(currentTest.testId, currentTest.status)}
              className="button"
              style={{ 
                backgroundColor: 'var(--success)',
                padding: isMobile ? 'var(--spacing-sm) var(--spacing-md)' : 'var(--spacing-md) var(--spacing-lg)',
                fontSize: isMobile ? 'var(--font-size-sm)' : 'var(--font-size-md)',
                fontWeight: '600',
                ...(isMobile && { width: '100%' })
              }}
            >
              View Results
            </button>
          )}
        </div>
      </div>

      {/* Description */}
      <div style={{ 
        marginBottom: 'var(--spacing-lg)',
        ...(isMobile && {
          marginBottom: 'var(--spacing-md)',
          flex: 1
        })
      }}>
        <p style={{
          color: 'var(--text-secondary)',
          margin: 0,
          fontSize: isMobile ? 'var(--font-size-sm)' : 'var(--font-size-md)',
          lineHeight: isMobile ? 1.5 : 1.6
        }}>
          {currentTest.description || 'Take this test to assess your knowledge and improve your skills.'}
        </p>
      </div>

      {/* Additional Details as Badges */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--spacing-sm)',
        marginBottom: 'var(--spacing-lg)',
        ...(isMobile && {
          marginBottom: 'var(--spacing-md)'
        })
      }}>
        {(currentTest.numQuestions || currentTest.totalQuestions) && (currentTest.status === 'UNSUBSCRIBED' || currentTest.status === 'SUBSCRIBED') && (
          <span className="badge" style={{
            backgroundColor: 'transparent',
            color: 'var(--primary-color)',
            padding: isMobile ? '4px 8px' : '6px 12px',
            fontSize: 'var(--font-size-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '4px' : '6px',
            borderRadius: isMobile ? 'var(--radius-sm)' : 'var(--radius-md)',
            border: '1px solid var(--primary-color)',
            fontWeight: '600'
          }}>
            📝 {currentTest.numQuestions || currentTest.totalQuestions} Questions
          </span>
        )}
        {currentTest.suggestedTimeInMinutes && currentTest.status !== 'COMPLETED' && (
          <span className="badge" style={{
            backgroundColor: 'transparent',
            color: 'var(--primary-color)',
            padding: isMobile ? '4px 8px' : '6px 12px',
            fontSize: 'var(--font-size-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '4px' : '6px',
            borderRadius: isMobile ? 'var(--radius-sm)' : 'var(--radius-md)',
            border: '1px solid var(--primary-color)',
            fontWeight: '600'
          }}>
            ⏱️ {currentTest.suggestedTimeInMinutes} min
          </span>
        )}
        {/*}
        {currentTest.category && (
          <span className="badge" style={{
            backgroundColor: 'var(--primary-light)',
            color: 'var(--primary-color)',
            padding: '6px 12px',
            fontSize: 'var(--font-size-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--primary-color)'
          }}>
            📚 {currentTest.category}
          </span>
        )} */}
        {currentTest.status === 'COMPLETED' && (
          <span className="badge" style={{
            backgroundColor: 'var(--success-light)',
            color: 'var(--success)',
            padding: isMobile ? '4px 8px' : '6px 12px',
            fontSize: 'var(--font-size-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '4px' : '6px',
            borderRadius: isMobile ? 'var(--radius-sm)' : 'var(--radius-md)',
            border: '1px solid var(--success)',
            fontWeight: '600'
          }}>
            🎯 Score: {calculateScore()}%
          </span>
        )}
      </div>
    </div>
  );
};

TestCard.propTypes = {
  test: PropTypes.shape({
    testId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    category: PropTypes.string,
    status: PropTypes.string,
    price: PropTypes.number,
    suggestedTimeInMinutes: PropTypes.number,
    numCorrectAnswers: PropTypes.number,
    numQuestions: PropTypes.number,
    difficulty: PropTypes.string,
    level: PropTypes.string
  }),
  tests: PropTypes.arrayOf(PropTypes.shape({
    testId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    category: PropTypes.string,
    status: PropTypes.string,
    price: PropTypes.number,
    suggestedTimeInMinutes: PropTypes.number,
    numCorrectAnswers: PropTypes.number,
    numQuestions: PropTypes.number,
    difficulty: PropTypes.string,
    level: PropTypes.string
  })),
  onStartTest: PropTypes.func.isRequired,
  onPayment: PropTypes.func,
  variant: PropTypes.oneOf(['compact', 'detailed'])
};

export default TestCard; 